const META_CAPI_STORAGE_KEY = 'winitMetaCapiContext'
/** Skip sessionStorage fbc/fbp fallback after this age (cookie-clear mid-session). */
const META_CAPI_SESSION_FALLBACK_MAX_AGE_MS = 30 * 60 * 1000

function isPriorFallbackFresh(prior) {
  if (!prior._capturedAt) return false
  return Date.now() - prior._capturedAt <= META_CAPI_SESSION_FALLBACK_MAX_AGE_MS
}

function readCookie(name) {
  if (typeof document === 'undefined') return undefined
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`),
  )
  if (!match?.[1]) return undefined
  try {
    return decodeURIComponent(match[1])
  } catch {
    return match[1]
  }
}

function buildFbcFromFbclid(fbclid) {
  return `fb.1.${Date.now()}.${fbclid}`
}

function readFbclidFromUrl() {
  if (typeof window === 'undefined') return undefined
  const fbclid = new URLSearchParams(window.location.search).get('fbclid')
  return fbclid?.trim() || undefined
}

export function captureMetaCapiContext(options = {}) {
  if (typeof window === 'undefined') return {}

  const existingFbc = readCookie('_fbc')
  const fbclid = readFbclidFromUrl()
  const meta_fbc = existingFbc || (fbclid ? buildFbcFromFbclid(fbclid) : undefined)
  const meta_fbp = readCookie('_fbp')
  const event_source_url = window.location.href.split('#')[0]

  const context = {
    ...(meta_fbc ? { meta_fbc } : {}),
    ...(meta_fbp ? { meta_fbp } : {}),
    ...(event_source_url ? { event_source_url } : {}),
  }

  if (options.persist !== false) {
    try {
      sessionStorage.setItem(
        META_CAPI_STORAGE_KEY,
        JSON.stringify({ ...context, _capturedAt: Date.now() }),
      )
    } catch {
      // ignore quota / privacy mode
    }
  }

  return context
}

function readStoredMetaCapiContext() {
  if (typeof window === 'undefined') return {}
  try {
    const raw = sessionStorage.getItem(META_CAPI_STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed === 'object') return parsed
  } catch {
    // ignore
  }
  return {}
}

/**
 * Re-reads cookies/URL, merges with prior sessionStorage values, and persists the result.
 * Side effect: writes to sessionStorage on every call (not a pure read).
 * Fresh cookies/URL win; prior fbc/fbp are kept only when cookies are absent and
 * the prior snapshot is younger than META_CAPI_SESSION_FALLBACK_MAX_AGE_MS.
 */
export function getStoredMetaCapiContext() {
  if (typeof window === 'undefined') return {}
  const prior = readStoredMetaCapiContext()
  const priorFresh = isPriorFallbackFresh(prior)
  const fresh = captureMetaCapiContext({ persist: false })
  const merged = {
    ...fresh,
    meta_fbc: fresh.meta_fbc || (priorFresh ? prior.meta_fbc : undefined),
    meta_fbp: fresh.meta_fbp || (priorFresh ? prior.meta_fbp : undefined),
    event_source_url: fresh.event_source_url || prior.event_source_url,
  }
  try {
    sessionStorage.setItem(
      META_CAPI_STORAGE_KEY,
      JSON.stringify({ ...merged, _capturedAt: Date.now() }),
    )
  } catch {
    // ignore
  }
  return merged
}

export function initMetaCapiContext() {
  captureMetaCapiContext()
}

/** Refreshes cookies before building so payment sync gets latest _fbc/_fbp. */
export function buildMetaCapiContextPayload() {
  const meta = getStoredMetaCapiContext()
  const payload = {}

  if (meta.meta_fbc) payload.fbc = meta.meta_fbc
  if (meta.meta_fbp) payload.fbp = meta.meta_fbp
  if (meta.event_source_url) payload.eventSourceUrl = meta.event_source_url

  return Object.keys(payload).length ? payload : undefined
}

export function attachMetaCapiContext(body) {
  const metaCapiContext = buildMetaCapiContextPayload()
  if (metaCapiContext) {
    body.metaCapiContext = metaCapiContext
  }
  return body
}
